import * as React from 'react'
import { createComponent, combineRules, applyTheme } from '@cloudflare/style-container'
import { Icon } from '@cloudflare/component-icon'
import { Input, IInputProps, InputTheme } from '@cloudflare/component-input'
import { Button } from '@cloudflare/component-button'

export type TKeyVals = [string, string][]
export type TKeyValEditorProps = {
  value: TKeyVals
  addNewChildren?: () => React.ReactElement<{}>
  onChange: (value: TKeyVals) => void
}
export class KeyValEditor extends React.Component<TKeyValEditorProps> {
  public static defaultProps: Partial<TKeyValEditorProps> = {
    addNewChildren: () => <span>Add New</span>,
  }

  public el: HTMLDivElement | null

  constructor(props: TKeyValEditorProps) {
    super(props)
    this.onChange = this.onChange.bind(this)
    this.onAddNewRowClick = this.onAddNewRowClick.bind(this)
    this.onRemoveRowClick = this.onRemoveRowClick.bind(this)
    this.onRefAvailable = this.onRefAvailable.bind(this)
  }

  componentDidUpdate() {
    if (this.isFirstValueEmpty()) {
      const alreadyFocused = this.el.querySelectorAll('input:focus').length > 0
      if (!alreadyFocused) {
        const input = this.el.querySelector('input')
        if (input) {
          input.focus()
        }
      }
    }
  }

  render() {
    const { value, addNewChildren } = this.props

    return (
      <div ref={this.onRefAvailable}>
        <KeyValEditorNewWrapper>
          <Button type="primary" onClick={this.onAddNewRowClick}>
            {addNewChildren()}
          </Button>
        </KeyValEditorNewWrapper>
        <KeyValEditorWrapper>
          {value.map(([key, val], i) => (
            <KeyValEditorRow key={i}>
              <KeyValEditorInputWrapper>
                <KeyValEditorKey
                  data-type="key"
                  data-key={key}
                  data-value={val}
                  name={`${key}-key`}
                  value={key}
                  onChange={this.onChange}
                />
              </KeyValEditorInputWrapper>
              <KeyValEditorInputWrapper>
                <KeyValEditorVal
                  data-type="value"
                  data-key={key}
                  data-value={val}
                  name={`${key}-value`}
                  value={val}
                  onChange={this.onChange}
                />
              </KeyValEditorInputWrapper>
              <KeyValEditorActionsWrapper>
                <KeyValEditorActionWrapper data-key={key} onClick={this.onRemoveRowClick}>
                  <Icon type="remove" label="Remove Key/Val Pair" />
                </KeyValEditorActionWrapper>
              </KeyValEditorActionsWrapper>
            </KeyValEditorRow>
          ))}
        </KeyValEditorWrapper>
      </div>
    )
  }

  isFirstValueEmpty() {
    return (this.props.value[0] || ([] as [string, string])).every(v => v === '')
  }

  addNewRow(key = '', val = '') {
    const result = [[key, val]] as TKeyVals

    for (let val of this.props.value) {
      result.push(val)
    }

    this.props.onChange(result)
  }

  onChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { key: prevKey, value: prevValue, type } = e.target.dataset
    const newKey = type === 'key' ? e.target.value : prevKey
    const newValue = type === 'value' ? e.target.value : prevValue
    this.props.onChange(
      this.props.value.map(([currKey, currVal]) => {
        if (prevKey === currKey) {
          return [newKey, newValue] as [string, string]
        }

        return [currKey, currVal] as [string, string]
      }),
    )
  }

  onAddNewRowClick() {
    this.addNewRow()
  }

  onRemoveRowClick(e: React.MouseEvent<HTMLSpanElement>) {
    const { key } = e.currentTarget.dataset
    this.props.onChange(this.props.value.filter(([aKey]) => aKey !== key))
  }

  onRefAvailable(el: HTMLDivElement | null) {
    this.el = el
  }
}

export const KeyValEditorWrapper = createComponent(({ theme }) => ({
  display: 'table',
  width: '100%',
}))
export const KeyValEditorRow = createComponent(({ theme }) => ({
  display: 'table-row',
  '&:first-child > *': {
    borderBottom: `solid 1px ${theme ? theme.colors.gray[8] : '#ebebeb'}`,
  },
}))

export const SvgIconActionWrapperRule = () => ({
  '& svg': {
    display: 'inline-block',
    boxSizing: 'border-box',
    fill: '#555',
    verticalAlign: 'middle',
    padding: '2px',
    marginTop: '-4px',
    cursor: 'pointer',
  },
})
export const KeyValEditorNewWrapper = createComponent(
  combineRules(SvgIconActionWrapperRule, ({ theme }: { theme?: any }) => ({
    '& button': {
      display: 'block',
      width: '100%',
    },
    '& svg': {
      fill: '#fff',
    },
  })),
)
export const KeyValEditorInputWrapperRule = ({ theme }: { theme?: any }) => ({
  display: 'table-cell',
  padding: '0.2rem 0',
  borderBottom: `solid 1px ${theme ? theme.colors.gray[8] : '#ebebeb'}`,
  borderRight: `solid 1px ${theme ? theme.colors.gray[8] : '#ebebeb'}`,
  '&:last-child': {
    paddingLeft: '0.2rem 0.5rem',
  },
})

export const KeyValEditorInputWrapper = createComponent(KeyValEditorInputWrapperRule)
export const KeyValEditorActionsWrapper = createComponent(
  combineRules(KeyValEditorInputWrapperRule, () => ({
    textAlign: 'right',
    borderRight: 'none',
  })),
)

export const KeyValEditorActionWrapper = createComponent(
  combineRules(
    SvgIconActionWrapperRule,
    (props: {
      'data-key': string
      onClick: (e: React.MouseEvent<HTMLDivElement>) => void
    }) => ({}),
  ),
  'span',
  ['onClick', 'data-key'],
)

export const KeyValEditorInputRule = ({
  theme,
}: { theme?: any; 'data-type': string } & IInputProps) => ({
  border: 'none',
  padding: '0.5rem',
  boxSizing: 'border-box',
  width: '100%',
  borderRight: `dotted ${theme ? theme.ash : '#ccc'} 1px !important`,
  margin: '0',
  // transition: 'transform 0.1s ease-out',
  // '&:focus': {
  //   outline: 'none',
  //   transform: 'scale(1.1)',
  //   boxShadow: '0 0 15px rgba(0, 0, 0, 0.12)',
  // },
})

export const KeyValEditorKey = createComponent(KeyValEditorInputRule, 'input', [
  'onChange',
  'value',
  'data-type',
  'data-key',
  'data-value',
  'placeholder',
])

export const KeyValEditorVal = createComponent(KeyValEditorInputRule, 'input', [
  'onChange',
  'value',
  'data-type',
  'data-key',
  'data-value',
  'placeholder',
])



// WEBPACK FOOTER //
// ./node_modules/@cloudflare/cf-component-key-val-editor/src/KeyValEditor.tsx